import { useState } from 'react';
import { DeckZone } from './components/deck/DeckZone.js';
import { SearchZone } from './components/search/SearchZone.js';
import { useDeckStore } from './stores/deck-store.js';
import { CardPreviewProvider } from './contexts/CardPreviewContext.js';
import { CardDetailModal } from './components/modal/CardDetailModal.js';
import { useAuthStore } from './stores/auth-store.js';
import { CollectionView } from './components/collection/CollectionView.js';
import { usePlaytesterStore } from './stores/playtester-store.js';
import PlaytesterView from './components/playtester/PlaytesterView.js';

type MainTab = 'editor' | 'collection';
type EditorTab = 'deck' | 'search';

const CLIENT_VERSION = 'v1.5.0';

export default function App() {
  const { user, isAuthenticated, isLoading, signInWithGoogle, signOut } = useAuthStore();
  const [activeMainTab, setActiveMainTab] = useState<MainTab>('collection');
  const [activeEditorTab, setActiveEditorTab] = useState<EditorTab>('deck');
  const playtesterIsActive = usePlaytesterStore((s) => s.isActive);

  const totalCards = useDeckStore((s) => s.entries.reduce((sum, e) => sum + e.quantity, 0));
  const isImportLoading = useDeckStore((s) => s.isImportLoading);

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-950 flex flex-col items-center justify-center text-gray-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 animate-pulse font-medium text-sm">Initializing AetherForge...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="h-screen bg-gray-950 text-gray-100 flex flex-col justify-between overflow-y-auto">
        <div />
        <div className="max-w-md w-full mx-auto px-6 py-12 flex flex-col items-center">
          {/* Logo / Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-tr from-orange-600 to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-950/50">
              <span className="text-xl font-black text-black">A</span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-orange-400 via-amber-200 to-white bg-clip-text text-transparent">
              AetherForge MTG
            </h1>
          </div>

          {/* Premium Card */}
          <div className="w-full bg-gray-900/60 backdrop-blur-xl border border-gray-800/80 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-300" />
            
            <h2 className="text-xl font-semibold mb-3 text-white">Commander Companion</h2>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              A professional deck building and analytics companion. Perform deep synergy audits, discover strategic portfolio gaps, and synchronize seamlessly with Moxfield.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <span className="text-orange-500 mt-1 text-base">✦</span>
                <div>
                  <h3 className="text-sm font-medium text-gray-200">Dynamic EDHREC Synergy</h3>
                  <p className="text-xs text-gray-500">Audit your decks against all 385+ EDHREC themes dynamically.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-500 mt-1 text-base">✦</span>
                <div>
                  <h3 className="text-sm font-medium text-gray-200">Strategic Portfolio Gaps</h3>
                  <p className="text-xs text-gray-500">Uncover unrepresented archetypes and themes across your collection.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-500 mt-1 text-base">✦</span>
                <div>
                  <h3 className="text-sm font-medium text-gray-200">Instant Moxfield Sync</h3>
                  <p className="text-xs text-gray-500">Import and sync your decks directly via proxy-accelerated fetching.</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => void signInWithGoogle()}
              className="w-full py-3 px-4 bg-white hover:bg-gray-100 text-gray-950 font-semibold rounded-xl flex items-center justify-center gap-3 transition shadow-lg shadow-white/5"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
              </svg>
              Sign In with Google
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-6 border-t border-gray-900/60 text-center shrink-0">
          <p className="text-xs text-gray-600 font-mono">AetherForge • {CLIENT_VERSION}</p>
        </footer>
      </div>
    );
  }

  const tabBtn = (tab: EditorTab, label: string, badge?: number) => (
    <button
      onClick={() => setActiveEditorTab(tab)}
      className={`flex-1 py-2 text-sm font-medium transition-colors border-b-2 ${
        activeEditorTab === tab
          ? 'border-orange-500 text-orange-400'
          : 'border-transparent text-gray-500 hover:text-gray-300'
      }`}
    >
      {label}
      {badge !== undefined && badge > 0 && (
        <span className="ml-1.5 text-xs bg-gray-700 text-gray-400 rounded-full px-1.5 py-0.5">
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="h-screen bg-gray-950 text-gray-100 flex flex-col overflow-hidden">
      {/* Top Navbar */}
      <header className="bg-gray-900/40 backdrop-blur border-b border-gray-800/80 px-6 py-3 shrink-0 flex items-center justify-between z-10">
        <div className="flex items-center gap-8">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-gradient-to-tr from-orange-600 to-amber-400 rounded-lg flex items-center justify-center shadow shadow-orange-950">
              <span className="text-sm font-black text-black">A</span>
            </div>
            <span className="font-bold tracking-tight text-white text-base">AetherForge</span>
          </div>

          {/* Navigation Tabs */}
          <nav className="flex items-center gap-1.5 bg-gray-950/80 p-1 rounded-xl border border-gray-800/65">
            <button
              onClick={() => setActiveMainTab('editor')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                activeMainTab === 'editor'
                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                  : 'text-gray-400 hover:text-gray-200 border border-transparent'
              }`}
            >
              Deck Builder
            </button>
            <button
              onClick={() => setActiveMainTab('collection')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                activeMainTab === 'collection'
                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                  : 'text-gray-400 hover:text-gray-200 border border-transparent'
              }`}
            >
              Collection & Analytics
            </button>
          </nav>
        </div>

        {/* User Stats / Profile & Version */}
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-600 font-mono inline">{CLIENT_VERSION}</span>
          
          {user && (
            <div className="flex items-center gap-3">
              {user.photoURL && (
                <img
                  src={user.photoURL}
                  alt={user.displayName || 'User'}
                  className="w-7 h-7 rounded-full border border-gray-700/80"
                />
              )}
              <span className="text-xs font-medium text-gray-300 hidden sm:inline">{user.displayName}</span>
              <button
                onClick={() => void signOut()}
                className="px-2.5 py-1 text-xs border border-gray-800 hover:bg-gray-800/60 rounded-lg text-gray-400 hover:text-white transition"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Area */}
      <CardPreviewProvider>
        {activeMainTab === 'editor' ? (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden relative">
            {/* Mobile tab bar for sub-editor view — hidden on lg+ */}
            <div className="flex lg:hidden border-b border-gray-800 shrink-0">
              {tabBtn('deck', 'Deck', totalCards)}
              {tabBtn('search', 'Search')}
            </div>

            <main className="flex flex-1 min-h-0 overflow-hidden">
              {/* Deck Zone — always visible on lg+, tab-controlled on mobile */}
              <div className={`${activeEditorTab === 'deck' ? 'flex' : 'hidden'} lg:flex flex-col h-full shrink-0`}>
                <DeckZone />
              </div>

              {/* Search Zone */}
              <div className={`${activeEditorTab === 'search' ? 'flex' : 'hidden'} lg:flex flex-col flex-1 min-w-0 h-full`}>
                <SearchZone />
              </div>
            </main>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden relative">
            <CollectionView onOpenEditor={() => setActiveMainTab('editor')} />
          </div>
        )}
        <CardDetailModal onOpenEditor={() => { setActiveMainTab('editor'); setActiveEditorTab('deck'); }} />
      </CardPreviewProvider>

      {/* Full-page loading overlay during Moxfield import */}
      {isImportLoading && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-300">Loading recommendations...</p>
          </div>
        </div>
      )}

      {/* Playtester overlay — fixed full-screen, z-50, rendered above everything */}
      {playtesterIsActive && <PlaytesterView />}
    </div>
  );
}
